import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** Weekly overdue corrective-action digest schedule, in the company's own timezone. */
export const CorrectiveActionDigestSetting = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({ enabled: true, dayOfWeek: 1, hour: 7, timezone: 'UTC' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance.get('/companies/corrective-action-digest')
      .then(({ data }) => setSettings(data))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/companies/corrective-action-digest', {
        enabled: settings.enabled,
        dayOfWeek: Number(settings.dayOfWeek),
        hour: Number(settings.hour),
      });
      toast.success(t('digestSetting.saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('digestSetting.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="digest-setting-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          {t('digestSetting.title')}
        </CardTitle>
        <CardDescription>
          {t('digestSetting.description', { timezone: settings.timezone })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => setSettings((prev) => ({ ...prev, enabled: v }))}
            data-testid="digest-enabled-switch"
          />
          <Label>{t('digestSetting.enabled')}</Label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>{t('digestSetting.day')}</Label>
            <Select
              value={String(settings.dayOfWeek)}
              onValueChange={(v) => setSettings((prev) => ({ ...prev, dayOfWeek: v }))}
            >
              <SelectTrigger data-testid="digest-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map((day, idx) => (
                  <SelectItem key={day} value={String(idx)}>{t(`digestSetting.days.${day}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('digestSetting.hour')}</Label>
            <Select
              value={String(settings.hour)}
              onValueChange={(v) => setSettings((prev) => ({ ...prev, hour: v }))}
            >
              <SelectTrigger data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, h) => (
                  <SelectItem key={h} value={String(h)}>{`${String(h).padStart(2, '0')}:00`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="save-digest-btn">
          {saving ? t('digestSetting.saving') : t('digestSetting.save')}
        </Button>
        <p className="text-xs text-gray-500">{t('digestSetting.emptyNote')}</p>
      </CardContent>
    </Card>
  );
};

export default CorrectiveActionDigestSetting;
